<template>
  <div class="p-field">
    <label v-if="label" :for="id" :class="{ 'p-error': errorMessage }">
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>
    <div
      class="p-chips-wrapper"
      :class="[
        {
          'p-rtl': rtl,
          'p-responsive': responsive,
          [`p-chip-${size}`]: size
        }
      ]"
    >
      <Chip
        :id="id"
        :label="chipLabel"
        :icon="icon"
        :image="image"
        :removable="removable"
        :remove-icon="removeIcon"
        :class="[
          {
            'p-invalid': isInvalid,
            'p-disabled': disabled
          }
        ]"
        :disabled="disabled"
        :aria-label="ariaLabel"
        :aria-labelledby="ariaLabelledby"
        :aria-invalid="isInvalid"
        :aria-required="required"
        :aria-describedby="ariaDescribedBy"
        @remove="onRemove"
        @click="onClick"
      >
        <template v-if="icon" #icon>
          <i :class="icon"></i>
        </template>
        <template v-if="removable" #removeicon>
          <button
            type="button"
            :aria-label="removeAriaLabel || t('common.remove')"
            :disabled="disabled"
            class="p-chip-remove-btn"
            @click="onRemove"
          >
            <i :class="removeIcon"></i>
          </button>
        </template>
        <slot></slot>
      </Chip>
    </div>
    <div v-if="showErrorMessages" class="p-error-messages">
      <small v-if="helperText" :id="helperTextId" class="p-error block">{{ helperText }}</small>
      <small
        v-for="(error, index) in localizedErrors"
        :key="index"
        :id="`${id}-error-${index}`"
        class="p-error block"
      >
        {{ error }}
      </small>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import Chip from 'primevue/chip'
import { useFormContext } from '../../../../composables/useFormContext'
import { useLocalization } from '../../../../composables/useLocalization'

interface Props {
  modelValue?: any
  label?: string
  chipLabel?: string
  icon?: string
  image?: string
  removable?: boolean
  removeIcon?: string
  removeAriaLabel?: string
  disabled?: boolean
  required?: boolean
  helperText?: string
  errorMessage?: string
  ariaLabel?: string
  ariaLabelledby?: string
  id?: string
  invalid?: boolean
  size?: 'small' | 'large'
  rtl?: boolean
  responsive?: boolean
  validationRules?: Array<{
    validator: (value: any) => boolean | Promise<boolean>
    message: string
  }>
  validateOnBlur?: boolean
  validateOnInput?: boolean
  validateOnChange?: boolean
  showErrors?: boolean
  name?: string
  formContext?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  label: undefined,
  chipLabel: undefined,
  icon: undefined,
  image: undefined,
  removable: false,
  removeIcon: 'pi pi-times',
  removeAriaLabel: undefined,
  disabled: false,
  required: false,
  helperText: '',
  errorMessage: undefined,
  ariaLabel: undefined,
  ariaLabelledby: undefined,
  id: () => `one-chip-${Math.random().toString(36).substr(2, 9)}`,
  invalid: false,
  size: undefined,
  rtl: false,
  responsive: false,
  validationRules: () => [],
  validateOnBlur: true,
  validateOnInput: false,
  validateOnChange: true,
  showErrors: true,
  name: undefined,
  formContext: true
})

const emit = defineEmits<{
  'update:modelValue': [value: any]
  remove: [event: Event]
  click: [event: Event]
  'validation-error': [errors: string[]]
  'validation-success': []
}>()

// Form Context
const { formState } = useFormContext()
const { t } = useLocalization()

// Local State
const localValidationErrors = ref<string[]>([])
const isDirty = ref(false)
const isValidating = ref(false)

const currentErrors = computed(() => {
  if (formState && props.name) {
    return formState.errors.value[props.name] || []
  }
  return localValidationErrors.value
})

const localizedErrors = computed(() => {
  return currentErrors.value.map(error => {
    if (error.startsWith('validation.')) {
      return t(error)
    }
    if (error.toLowerCase().includes('required')) {
      return t('validation.required')
    }
    return error
  })
})

const isInvalid = computed(() => {
  return props.invalid || (currentErrors.value.length > 0 && isDirty.value)
})

const helperTextId = computed(() => `${props.id}-helper`)

const ariaDescribedBy = computed(() => {
  const ids = []
  if (props.helperText) ids.push(helperTextId.value)
  currentErrors.value.forEach((_, index) => ids.push(`${props.id}-error-${index}`))
  return ids.join(' ')
})

const showErrorMessages = computed(() => {
  return props.showErrors && (!!props.helperText || (currentErrors.value.length > 0 && isDirty.value))
})

const validate = async (): Promise<boolean> => {
  if (formState && props.name) {
    return formState.validateField(props.name)
  }

  isValidating.value = true
  try {
    const errors: string[] = []
    const value = props.modelValue

    for (const rule of props.validationRules) {
      let isValid = true
      const message = rule.message

      if (rule.validator) {
        isValid = await rule.validator(value)
      }

      if (!isValid && message) {
        errors.push(message)
      }
    }

    localValidationErrors.value = errors
    if (errors.length > 0) {
      emit('validation-error', errors)
      return false
    }

    emit('validation-success')
    return true
  } finally {
    isValidating.value = false
  }
}

const onRemove = (event: Event) => {
  if (!props.disabled) {
    emit('remove', event)
    isDirty.value = true
    if (props.validateOnChange) {
      validate()
    }
  }
}

const onClick = (event: Event) => {
  if (!props.disabled) {
    emit('click', event)
  }
}

// Initialize form context
onMounted(() => {
  if (formState && props.name && props.formContext && props.validationRules.length > 0) {
    formState.addValidation(props.name, props.validationRules)
  }
})

// Watch for external validation triggers
watch(() => props.invalid, (newValue) => {
  if (newValue) {
    isDirty.value = true
  }
})

defineExpose({
  validate,
  validationErrors: currentErrors,
  isDirty,
  isValidating,
  isValid: computed(() => currentErrors.value.length === 0)
})
</script>

<style scoped>
.p-field {
  @apply mb-4;
}

.p-chips-wrapper {
  @apply flex flex-wrap gap-2;
}

.p-chip-small {
  @apply text-sm;
}

.p-chip-large {
  @apply text-lg;
}

.p-rtl {
  direction: rtl;
}

.p-responsive {
  @apply w-full;
}

.p-error {
  @apply text-red-500 text-sm mt-1;
}

.p-chip-remove-btn {
  @apply p-1 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500;
}

.p-disabled .p-chip-remove-btn {
  @apply cursor-not-allowed opacity-50;
}
</style>
